import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { toast } from "@/components/ui/sonner";
import { useAuth } from '@/contexts/AuthContext';
import { useGamification } from '@/hooks/useGamification';
import { useRealtimeChannel } from '@/hooks/useRealtimeChannel';
import { supabase } from '@/integrations/supabase/client';
import { ROUTES } from '@/lib/routes';
import { useReducerWithDevtools } from '@/lib/useReducerWithDevtools';
import { projectDetailReducer, initialProjectDetailState } from '@/reducers/projectDetailReducer';

import type { Session, ProjectDetailProject, Tag, Folder } from '@/types/state';

const projectKeys = {
  all: (projectId: string) => ['project', projectId] as const,
  details: (projectId: string) => [...projectKeys.all(projectId), 'details'] as const,
  sessions: (projectId: string) => [...projectKeys.all(projectId), 'sessions'] as const,
  tags: (projectId: string) => [...projectKeys.all(projectId), 'tags'] as const,
  workspaceData: (workspaceId: string) => ['workspaceData', workspaceId] as const,
};

export const useProjectDetailState = (projectId: string | undefined) => {
  const [state, dispatch] = useReducerWithDevtools(projectDetailReducer, initialProjectDetailState, 'ProjectDetail');
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { awardPoints } = useGamification();
  const queryClient = useQueryClient();

  // --- Data Fetching with React Query ---

  const { data: project, isLoading: isLoadingProject } = useQuery({
    queryKey: projectKeys.details(projectId!),
    queryFn: async () => {
      const { data, error } = await supabase.from('projects').select('*, boards(workspace_id)').eq('id', projectId!).single();
      if (error) throw error;
      return data as ProjectDetailProject;
    },
    enabled: !!projectId && !!user,
  });

  const { data: sessions = [], isLoading: isLoadingSessions } = useQuery({
    queryKey: projectKeys.sessions(projectId!),
    queryFn: async () => {
      const { data } = await supabase.from('sessions').select('*').eq('project_id', projectId!).order('created_at', { ascending: false });
      return (data || []) as Session[];
    },
    enabled: !!projectId && !!user,
  });

  const { data: projectTags = [], isLoading: isLoadingProjectTags } = useQuery({
    queryKey: projectKeys.tags(projectId!),
    queryFn: async () => {
      const { data } = await supabase.from('project_tags').select('tags(id, name, color)').eq('project_id', projectId!);
      return (data?.map((pt: any) => pt.tags).filter(Boolean) || []) as Tag[];
    },
    enabled: !!projectId && !!user,
  });

  const workspaceId = (project as any)?.boards?.workspace_id;

  const { data: workspaceData, isLoading: isLoadingWorkspaceData } = useQuery({
    queryKey: projectKeys.workspaceData(workspaceId!),
    queryFn: async () => {
      const [foldersRes, tagsRes, projectsRes] = await Promise.all([
        supabase.from('folders').select('*').eq('workspace_id', workspaceId!).order('name'),
        supabase.from('tags').select('*').eq('workspace_id', workspaceId!).order('name'),
        supabase.from('projects').select('id, name, description, board_id, folder_id, archived, boards!inner(workspace_id)').eq('boards.workspace_id', workspaceId!).neq('id', projectId!).order('name'),
      ]);
      return {
        folders: (foldersRes.data || []) as Folder[],
        availableTags: (tagsRes.data || []) as Tag[],
        availableProjects: (projectsRes.data || []) as ProjectDetailProject[],
      };
    },
    enabled: !!workspaceId,
  });

  const { folders = [], availableTags = [], availableProjects = [] } = workspaceData || {};
  const loading = isLoadingProject || isLoadingSessions || isLoadingProjectTags || isLoadingWorkspaceData;

  // --- Realtime Subscriptions ---

  useRealtimeChannel({
    channelName: `project-details:${projectId}`,
    type: 'postgres_changes',
    event: '*',
    schema: 'public',
    table: 'projects',
    filter: `id=eq.${projectId}`,
    onMessage: () => queryClient.invalidateQueries({ queryKey: projectKeys.details(projectId!) }),
  });

  useRealtimeChannel({
    channelName: `project-sessions:${projectId}`,
    type: 'postgres_changes',
    event: '*',
    schema: 'public',
    table: 'sessions',
    filter: `project_id=eq.${projectId}`,
    onMessage: () => queryClient.invalidateQueries({ queryKey: projectKeys.sessions(projectId!) }),
  });

  // --- Mutations ---

  const createSessionMutation = useMutation<Session, Error, void>({
    mutationFn: async () => {
      const sessionName = `Session from ${format(new Date(), 'MMM d, yyyy')}`;
      const { data, error } = await supabase.from('sessions').insert({ project_id: projectId, name: sessionName }).select().single();
      if (error) throw error;
      return data as Session;
    },
    onSuccess: (data) => {
      awardPoints('session');
      navigate(`/sessions/${data.id}`);
    },
    onError: () => toast.error('Failed to create session'),
  });

  const updateProjectMutation = useMutation<void, Error, Partial<ProjectDetailProject>>({
    mutationFn: async (updates: Partial<ProjectDetailProject>) => {
      const { error } = await supabase.from('projects').update(updates).eq('id', projectId!);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.details(projectId!) });
      toast.success(`Project ${Object.keys(variables)[0]} updated`);
      if ('name' in variables) dispatch({ type: 'CLOSE_EDIT_NAME_DIALOG' });
      if ('description' in variables) dispatch({ type: 'CLOSE_EDIT_DESC_DIALOG' });
      if ('folder_id' in variables) dispatch({ type: 'CLOSE_FOLDER_DIALOG' });
    },
    onError: () => toast.error('Failed to update project'),
  });

  const deleteProjectMutation = useMutation<void, Error, void>({
    mutationFn: async () => {
      const { error } = await supabase.from('projects').delete().eq('id', projectId!);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Project deleted');
      if (project?.board_id) navigate(`/boards/${project.board_id}`);
      else navigate('/projects');
    },
    onError: () => toast.error('Failed to delete project'),
  });

  const updateSessionMutation = useMutation<void, Error, { sessionId: string; name: string }>({
    mutationFn: async ({ sessionId, name }) => {
      const { error } = await supabase.from('sessions').update({ name }).eq('id', sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.sessions(projectId!) });
      toast.success('Session name updated');
      dispatch({ type: 'CLOSE_EDIT_SESSION_DIALOG' });
    },
    onError: () => toast.error('Failed to update session name'),
  });

  const deleteSessionMutation = useMutation<void, Error, string>({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase.from('sessions').delete().eq('id', sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.sessions(projectId!) });
      toast.success('Session deleted');
      dispatch({ type: 'CLOSE_DELETE_SESSION_DIALOG' });
    },
    onError: () => toast.error('Failed to delete session'),
  });

  const moveSessionsMutation = useMutation<void, Error, { targetProjectId: string; sessionIds: string[] }>({
    mutationFn: async ({ targetProjectId, sessionIds }) => {
      const { error } = await supabase.from('sessions').update({ project_id: targetProjectId }).in('id', sessionIds);
      if (error) throw error;
    },
    onSuccess: (_, { sessionIds }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all(projectId!) });
      const targetProject = availableProjects.find(p => p.id === state.targetProjectId);
      toast.success(`Moved ${sessionIds.length} session(s) to ${targetProject?.name}`);
      dispatch({ type: 'CLEAR_SESSION_SELECTION' });
      dispatch({ type: 'CLOSE_MOVE_SESSION_DIALOG' });
    },
    onError: () => toast.error('Failed to move sessions'),
  });

  // --- Handlers ---

  const handleSaveName = useCallback(() => {
    if (!state.editedName.trim()) return toast.error('Project name cannot be empty');
    updateProjectMutation.mutate({ name: state.editedName.trim() });
  }, [state.editedName, updateProjectMutation]);

  const handleSaveDescription = useCallback(() => {
    updateProjectMutation.mutate({ description: state.editedDescription.trim() || null });
  }, [state.editedDescription, updateProjectMutation]);

  const handleArchiveProject = useCallback(() => {
    updateProjectMutation.mutate({ archived: !project?.archived });
  }, [project?.archived, updateProjectMutation]);

  const handleSaveSessionName = useCallback(() => {
    if (!state.editedSessionName.trim()) return toast.error('Session name cannot be empty');
    if (!state.selectedSession) return;
    updateSessionMutation.mutate({ sessionId: state.selectedSession.id, name: state.editedSessionName.trim() });
  }, [state.editedSessionName, state.selectedSession, updateSessionMutation]);

  const handleDeleteSession = useCallback(() => {
    if (!state.selectedSession) return;
    deleteSessionMutation.mutate(state.selectedSession.id);
  }, [state.selectedSession, deleteSessionMutation]);

  const handleMoveToFolder = useCallback(() => {
    updateProjectMutation.mutate({ folder_id: state.selectedFolderId });
  }, [state.selectedFolderId, updateProjectMutation]);

  const handleMoveSessions = useCallback(() => {
    if (!state.targetProjectId || state.selectedSessionIds.size === 0) return toast.error('Please select a target project');
    moveSessionsMutation.mutate({ targetProjectId: state.targetProjectId, sessionIds: Array.from(state.selectedSessionIds) });
  }, [state.targetProjectId, state.selectedSessionIds, moveSessionsMutation]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate(ROUTES.AUTH);
  }, [user, authLoading, navigate]);

  // --- Effects ---

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate(ROUTES.AUTH);
    if (!project && !loading) {
      toast.error('Project not found');
      navigate(ROUTES.DASHBOARD);
    }
  }, [user, authLoading, navigate, project, loading]);

  return {
    state,
    dispatch,
    project,
    sessions,
    projectTags,
    availableTags,
    folders,
    availableProjects,
    loading,
    createSession: createSessionMutation.mutate,
    isCreatingSession: createSessionMutation.isPending,
    handleEditName: () => dispatch({ type: 'OPEN_EDIT_NAME_DIALOG', payload: project?.name || '' }),
    handleSaveName,
    handleEditDescription: () => dispatch({ type: 'OPEN_EDIT_DESC_DIALOG', payload: project?.description || '' }),
    handleSaveDescription,
    isSaving: updateProjectMutation.isPending,
    handleDeleteProject: deleteProjectMutation.mutate,
    isDeleting: deleteProjectMutation.isPending,
    handleArchiveProject,
    handleEditSession: (session: Session) => dispatch({ type: 'OPEN_EDIT_SESSION_DIALOG', payload: session }),
    handleSaveSessionName,
    isSavingSession: updateSessionMutation.isPending,
    handleDeleteSession,
    isDeletingSession: deleteSessionMutation.isPending,
    handleMoveToFolder,
    handleMoveSessions,
    isMovingSessions: moveSessionsMutation.isPending,
    fetchProjectData: () => queryClient.invalidateQueries({ queryKey: projectKeys.all(projectId!) }),
  };
};
